import React, { useEffect, useState } from "react";
import {
  Card,
  Upload,
  Button,
  DatePicker,
  Input,
  message,
  Divider,
  Typography,
  Space,
  Row,
  Col,
  Form,
} from "antd";
import {
  UploadOutlined,
  PlusOutlined,
  DeleteOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useAppSelector, useAppDispatch } from "../../hooks/reduxHooks";
import {
  deletePersonalJournalApi,
  savePersonalJournalApi,
} from "../../services/personalJournal.service";
import { generateFormData } from "../../utils/generateFormData";
import { useNavigate } from "react-router-dom";
import ReactGA from "react-ga4";
import { set } from "lodash";

const { TextArea } = Input;
const { Text } = Typography;

interface PersonalJournalComponentInterface {
  data?: any;
  currentSkincare?: any;
}

const PersonalJournalComponent: React.FC<PersonalJournalComponentInterface> = ({
  data,
  currentSkincare,
}) => {
  console.log("data", data);
  const [entries, setEntries] = useState<any[]>([]);
  const profile = useAppSelector((state) => state.profile);
  const { first_name, survey_id } = profile;

  useEffect(() => {
    const newData = data?.map((entry: any) => ({
      ...entry,
      image: `${process.env.REACT_APP_API_DOMAIN}/storage/${entry.image}`,
      date: dayjs(entry.date),
      isSubmitted: true,
    }));
    console.log("newData", newData);
    setEntries(newData);
  }, [data]);

  const dispatch = useAppDispatch();

  const [newEntryImage, setNewEntryImage] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const addNewEntry = () => {
    const newEntry = {
      id: Date.now(),
      date: dayjs(),
      image: "",
      note: "",
      isSubmitted: false,
    };
    setEntries([...entries, newEntry]);
    setNewEntryImage(null);
    form.resetFields();
  };

  const deleteEntry = async (id: number) => {
    setLoading(true);
    dispatch(
      deletePersonalJournalApi({
        survey_id: survey_id,
        entry_id: id,
        successCallback: () => {
          console.log("Entry deleted successfully");
        },
        failureCallback: () => {
          console.log("Failed to delete entry");
        },
        finalCallback: () => {
          setLoading(false);
          console.log("Delete entry API call finished");
        },
      })
    );
    setEntries(entries.filter((entry: any) => entry.id !== id));
  };

  const handleImageUpload = (id: number, { fileList }: any) => {
    if (fileList.length > 0) {
      const file = fileList[0].originFileObj;
      if (file) {
        setFile(file);
        const reader = new FileReader();
        reader.onload = (e) => {
          if (e.target) {
            if (typeof e.target.result === "string") {
              setNewEntryImage(e.target.result);
              message.success("Photo uploaded successfully!");
            } else {
              setNewEntryImage(null);
              message.error("Failed to read image file.");
            }
          }
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const beforeUpload = (file: File) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      message.error("You can only upload JPG/PNG file!");
      return false;
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error("Image must smaller than 2MB!");
      return false;
    }
    return false; // Prevent automatic upload
  };

  const onFinish = (values: { date: dayjs.Dayjs; note: string }) => {
    const latestEntry = entries[entries.length - 1];
    const updatedEntries = entries.map((entry: any) =>
      entry.id === latestEntry.id
        ? {
          ...entry,
          date: values.date || dayjs(),
          note: values.note || "",
          image: newEntryImage ?? "", // Ensure image is always a string
          isSubmitted: true,
        }
        : entry
    );

    const payload = {
      date: values.date,
      note: values.note || "",
      isSubmitted: true,
    };

    saveEntry(payload);

    setEntries(updatedEntries);
  };

  const saveEntry = async (payload: any) => {
    setLoading(true);
    const formData = new FormData();

    // Add form fields
    if (survey_id) formData.append("survey_id", survey_id);
    formData.append("date", payload.date);
    formData.append("note", payload.note || "");
    if (file) {
      formData.append("image", file);
    }

    dispatch(
      savePersonalJournalApi({
        data: formData,
        finalCallback: () => {
          setLoading(false);
          setError(false);
        },
        failureCallback: () => {
          setLoading(false);
          setError(true);
        },
        successCallback: (responseData: any) => {
          setNewEntryImage(null);
          form.resetFields();
          message.success("Entry submitted successfully!");
        },
      })
    );
  };

  const getCurrentEntry = () => {
    return entries.find((entry: any) => !entry.isSubmitted);
  };

  const currentEntry = getCurrentEntry();

  return (
    <div
      style={{
        minHeight: "100vh",
      }}
    >
      <Row
        className="store-section skincare-solution missing_skincare"
        style={{ marginBottom: "24px" }}
      >
        <Col>
          <Card
            style={{
              borderRadius: "20px",
              border: "none",
            }}
            bodyStyle={{ padding: "24px 32px" }}
          >
            <h1 className="title store-card__title" style={{ marginBottom: 0 }}>
              {first_name}'s Skin Progress Journal
            </h1>
          </Card>
        </Col>
      </Row>
      <div className="personal-journal-card">
        {entries
          .filter((entry: any) => entry.isSubmitted)
          .map((entry: any) => (
            <div key={entry.id}>
              <Card
                style={{
                  borderRadius: "20px",
                  boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
                  border: "none",
                  position: "relative",
                }}
                bodyStyle={{ padding: "24px" }}
              >
                {entries.filter((e: any) => e.isSubmitted).length > 0 && (
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => deleteEntry(entry.id)}
                    style={{
                      position: "absolute",
                      top: "8px",
                      right: "8px",
                      zIndex: 1,
                    }}
                  />
                )}
                <div style={{ textAlign: "center", marginBottom: "16px" }}>
                  {entry.image && (
                    <img
                      src={entry.image}
                      alt="Skin photo"
                      style={{
                        width: "100%",
                        aspectRatio: "1 / 1",
                        borderRadius: "15px",
                        objectFit: "cover",
                        border: "3px solid #f0f0f0",
                      }}
                    />
                  )}
                </div>
                <Text
                  style={{
                    fontSize: "16px",
                    fontWeight: "normal",
                    display: "block",
                    textAlign: "left",
                    marginBottom: "16px",
                  }}
                >
                  {entry.date.format("M/D/YYYY")}
                </Text>
                <Divider style={{ margin: "13px 0" }} />
                <Text
                  style={{
                    fontSize: "14px",
                    lineHeight: "1.6",
                    color: "#666",
                    display: "block",
                  }}
                >
                  {entry.note}
                </Text>
              </Card>
            </div>
          ))}
        {currentEntry && (
          <div>
            <Card
             className="journal-form"
              style={{
                borderRadius: "20px",
                boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
                border: "none",
                position: "relative",
              }}
              bodyStyle={{ padding: "24px" }}
            >
              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                initialValues={{
                  date: dayjs(),
                  note: "",
                }}
              >
                <Form.Item
                  name="skinPhoto"
                  rules={[
                    {
                      required: true,
                      message: "Please upload an image",
                    },
                  ]}
                >
                  <div style={{ textAlign: "center", marginBottom: "16px" }}>
                    {newEntryImage ? (
                      <img
                        src={newEntryImage}
                        alt="Skin photo"
                        style={{
                          width: "100%",
                          aspectRatio: "1 / 1",
                          borderRadius: "15px",
                          objectFit: "cover",
                          border: "3px solid #f0f0f0",
                        }}
                      />
                    ) : (
                      <>
                      <Upload
                        accept="image/*"
                        showUploadList={false}
                        beforeUpload={beforeUpload}
                        onChange={(info) => handleImageUpload(currentEntry.id, info)}
                      >
                        <div
                          style={{
                            width: "95px",
                            height: "95px",
                            borderRadius: "50%",
                            background: "#87CEEB",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            margin: "0 auto",
                          }}
                        >
                          <div style={{ textAlign: "center" }}>
                            <UploadOutlined
                              style={{
                                fontSize: "60px",
                                color: "white",
                                display: "block",
                                marginBottom: "8px",
                              }}
                            />
                            
                          </div>
                        </div>
                      </Upload>
                       <p style={{ marginTop: "8px" }}>Upload Photo</p>
                             </>

                     
                    )}
                    
                  </div>
                </Form.Item>
                <Form.Item
                  // label="Today's Date"
                  // name="date"
                  // labelCol={{
                  //   style: {
                  //     color: "#999",
                  //     fontSize: "12px",
                  //     fontWeight: "500",
                  //   },
                  // }}
                >
                  <DatePicker
                      placeholder="Today's Date"
                    style={{
                      width: "100%",
                      height: "36px",
                      fontSize: "14px",
                    }}
                    format="M/D/YYYY"
                    className="jornal-date-picker"
                  />
                </Form.Item>
                <Form.Item
                  label=""
                  name="note"
                  labelCol={{
                    style: {
                      color: "#999",
                      fontSize: "12px",
                      fontWeight: "500",
                    },
                  }}
                >
                  <TextArea
                    placeholder="How's Skin Feeling..."
                    autoSize={{ minRows: 3, maxRows: 4 }}
                    style={{
                      fontSize: "14px",
                      lineHeight: "1.5",
                    }}
                    className="jornal-text-area"
                  />
                </Form.Item>
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="journal-btn"
                    block
                    style={{
                      height: "40px",
                      borderRadius: "8px",
                      background: "#b5cc4c",
                      color: "#191919",
                      border: "none",
                      fontSize: "14px",
                      fontWeight: "600",
                      boxShadow: "0 4px 12px rgba(154, 205, 50, 0.3)",
                    }}
                    loading={loading}
                  >
                    Submit Entry
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </div>
        )}
        {/* Add New Entry Button */}
        {!currentEntry && (
          <Col xs={24} sm={12} md={8} lg={5}>
            <Card
              style={{
                borderRadius: "20px",
                // boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
                border: "none",
                background: "transparent",
                // background: "linear-gradient(135deg, #9ACD32, #7CB342)",
                minHeight: "400px",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                cursor: "pointer",
              }}
              bodyStyle={{
                padding: "24px",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                width: "100%",
                marginTop: "20px",
              }}
              onClick={addNewEntry}
            >
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "50%",
                    // background: "rgba(255,255,255,0.2)",
                    background: "#b5cc4c",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "20px auto 16px auto",
                  }}
                >
                  {loading ? (
                    <LoadingOutlined
                      style={{ fontSize: "24px", color: "white" }}
                    />
                  ) : (
                    <PlusOutlined
                      style={{ fontSize: "24px", color: "white" }}
                    />
                  )}
                </div>
              </div>
            </Card>
          </Col>
        )}
      </div>
      <div className="footer-cta">
        <Button
          className="btn-primary cta-btn"
          onClick={() => {
            navigate("/store/earn-cash-back");
            ReactGA.event({
              category: "Store",
              action: `Go to The Appreciate You button clicked`,
              label: "",
              nonInteraction: true, // optional, true/false
              transport: "xhr",
            });
          }}
        >
          Go to Earn Passive Cash
        </Button>
      </div>
    </div>
  );
};

export default PersonalJournalComponent;
